import React, { useRef, useState } from 'react';

import DemmiButton from '@demmi-ui/Button';
import DemmiCardArea from '@demmi-ui/Modal/ModalArea';
import DemmiCardButtonArea from '@demmi-ui/Modal/ModalButtonArea';
import DemmiModalCore, { DemmiModalCoreRef } from '@demmi-ui/Modal/ModalCore';

interface PropsCustom extends Base {
  triggerEl: JSX.Element;
}
interface PropsDefault extends Base {
  triggerButtonText: string;
  triggerButtonIcon: string;
  triggerButtonVariant?: 'delete' | 'save';
}

interface Base {
  titleText: string;
  itemText?: string;
  descriptionText?: string;
  customView?: JSX.Element;
  onClose?: () => void;
  onConfirm: () => void;
}

export const isCustomTrigger = (
  props: PropsDefault | PropsCustom,
): props is PropsCustom => {
  return 'triggerEl' in props;
};

const ModalBoolean: React.FC<PropsDefault | PropsCustom> = props => {
  const {
    titleText,
    itemText,
    descriptionText,
    customView,
    onClose,
    onConfirm,
  } = props;
  const CSSBlock = 'modal-boolean';
  const modalRef = useRef<DemmiModalCoreRef>(null);
  const [isOpen, setIsOpen] = useState(false);

  const triggerEl = isCustomTrigger(props) ? (
    React.cloneElement(props.triggerEl, {
      onClick: () => setIsOpen(true),
    })
  ) : (
    <DemmiButton
      text={props.triggerButtonText}
      faIcon={props.triggerButtonIcon}
      variant={props.triggerButtonVariant}
      onClick={() => setIsOpen(true)}
    />
  );

  const gracefulExit = (postExit: () => void = () => {}) => {
    modalRef.current ? modalRef.current.gracefulClose(postExit) : postExit();
    setTimeout(() => {
      setIsOpen(false);
    }, 500);
  };

  const buttons = [
    <DemmiButton
      key={0}
      text="No"
      outlined
      onClick={() => gracefulExit(onClose)}
    />,
    <DemmiButton
      key={1}
      text="Yes"
      variant="delete"
      onClick={() => gracefulExit(() => onConfirm())}
    />,
  ];

  return (
    <>
      {triggerEl}
      <DemmiModalCore
        ref={modalRef}
        isOpen={isOpen}
        onExited={() => {
          setIsOpen(false);
          onClose && onClose();
        }}
        contentClassName={CSSBlock}
        allowBackdropClose={false}>
        <DemmiCardArea isDark>
          <div className={`${CSSBlock}__text`}>{titleText}</div>
          {itemText && <div className={`${CSSBlock}__name`}>{itemText}</div>}
          {descriptionText && (
            <div className={`${CSSBlock}__description`}>{descriptionText}</div>
          )}
          {customView}
        </DemmiCardArea>
        <DemmiCardButtonArea buttons={buttons} />
      </DemmiModalCore>
    </>
  );
};

export default ModalBoolean;
